/**
 * Subscription › Agreement (the owner, 2 Nov 2026): the customer's one agreement, on one page. What they take now, what waits for its date, the exceptions current and upcoming, and everything that
 * ever changed. Reads api.agreement_panel. Amend and Rejoin open beside the list, so the exceptions stay in view. The other tools keep working where they are until the owner agrees to take them away.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, CheckCircle2, CircleSlash, History as HistoryIcon, PauseCircle, PencilLine, RotateCcw } from 'lucide-react';
import { rpc } from '../../lib/supabase';
import { useIdempotencyKey } from '../../lib/idempotency';
import { Button, EmptyState, ErrorState, LoadingRows, Panel, PanelHeader, StatusBadge, cx, plainError } from '../../ui/primitives';
import { ConfirmDialog } from '../../ui/overlays';
import { toast } from '../../ui/toast';
import { useOpenChange, ChangeDeliveryButton } from './Customer360Overview';
import type { Customer360 } from './customerDescriptor';
import { AgreementForm } from './AgreementForm';
import { AGREEMENT_KEY, dayWord, everyWords, exceptionRows, historyWhat, spanWords, useAgreementPanel, type AgreementCard, type AgreementPanelRead, type DayState, type ExceptionRow, type RunException } from './agreementRead';

const chip = 'inline-flex items-center whitespace-nowrap rounded-full border border-line px-2 text-small leading-6 text-ink';

function Lines({ card }: { card: AgreementCard }) {
  if (card.lines.length === 0) return <p className="text-small text-ink-muted">No items on this agreement.</p>;
  return (
    <ul className="grid gap-1">
      {card.lines.map((l) => (
        <li key={l.variantId} className="flex flex-wrap items-center gap-2 text-body">
          <span className={chip}>{l.code} ×{l.quantity}</span>
          <span className="text-ink-muted">{everyWords(l)}</span>
        </li>))}
    </ul>);
}

const tomorrowWord: Record<DayState, string> = { OPEN: 'tomorrow open', PREPARED: 'tomorrow prepared, changes go straight', FROZEN: 'tomorrow closed', LOADED: 'tomorrow loaded', DELIVERED: 'tomorrow delivered', PAST: 'tomorrow closed' };
const STATUS_WORD: Record<string, string> = { ACTIVE: 'Active', PAUSED: 'Paused', LEFT: 'Left', INACTIVE: 'Inactive', STARTING: 'Starting', DRAFT: 'Draft', RETIRED: 'Retired' };

export function AgreementHead({ data, door, onDoor }: { data: AgreementPanelRead; door: string | null; onDoor: (d: string | null) => void }) {
  const st = data.customer.status;
  const doors = data.doors.filter((x) => x.hasAgreement);
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line px-4 py-3">
      <span className="text-section font-semibold text-ink">Agreement{data.now ? <span className="code-text ml-2 font-normal text-ink-muted">{data.now.code}</span> : null}</span>
      <StatusBadge tone={st.status === 'ACTIVE' ? 'accepted' : st.status === 'PAUSED' ? 'queued' : 'neutral'} icon={st.status === 'ACTIVE' ? CheckCircle2 : st.status === 'PAUSED' ? PauseCircle : CircleSlash}>
        {STATUS_WORD[st.status] ?? st.status}{st.from && st.status !== 'ACTIVE' ? ` since ${dayWord(st.from)}` : ''}
      </StatusBadge>
      {doors.length > 1 ? (
        <span role="group" aria-label="For which address" className="inline-flex items-center gap-1 text-small text-ink-muted">For:
          {doors.map((x) => (
            <button key={x.id} type="button" aria-pressed={(door ?? data.door) === x.id} onClick={() => onDoor(x.id)}
              className={cx('h-7 rounded-[var(--radius-control)] border px-2', (door ?? data.door) === x.id ? 'border-brand bg-brand-soft text-brand' : 'border-line')}>{x.label}</button>))}
        </span>) : null}
      <span className="ml-auto text-small text-ink-muted"><CalendarClock size={13} aria-hidden className="mr-1 inline" />Cut-off today {data.cutoff.time.slice(0, 5)} · {tomorrowWord[data.cutoff.tomorrowState]}</span>
    </div>);
}

type Pending =
  | { kind: 'exception'; row: ExceptionRow }
  | { kind: 'waiting'; card: AgreementCard }
  | null;

/** What the cancel button on an exception row says and sends. A hold is resumed; a status change is the Overview's. */
export function cancelOf(row: ExceptionRow, customerId: string): { label: string; body: Record<string, unknown> } | null {
  const x = row.source[0]!;
  if (x.kind === 'RUN') {
    const rest = row.source.some((s) => ['FROZEN', 'LOADED', 'DELIVERED'].includes((s as RunException).firstState));
    return { label: rest ? 'Cancel rest' : 'Cancel', body: { customerId, kind: 'RUN', ids: row.source.flatMap((s) => (s as RunException).ids) } };
  }
  if (x.kind === 'EXTRA') return x.mode === 'ROUND' ? { label: 'Cancel', body: { customerId, kind: 'EXTRA', orderId: x.orderId } } : null;
  if (x.kind === 'NOTE' || x.kind === 'ELSEWHERE') return x.changeId ? { label: 'Cancel', body: { customerId, kind: x.kind, changeId: x.changeId } } : null;
  if (x.kind === 'APPROVAL') return { label: 'Cancel', body: { customerId, kind: 'APPROVAL', changeId: x.changeId } };
  return null;
}

/** A customer can be active with no agreement (the last was cancelled): the owner, 2026-11-03, the standing order starts again from here. */
export function startWords(data: Pick<AgreementPanelRead, 'last'>, left: boolean, paused: boolean): string {
  const verb = left || paused ? 'Rejoin' : 'Start agreement';
  return data.last ? `The last one ended. ${verb} starts from its items; add or change any product there.` : `${verb} sets the products and days.`;
}

/** The first view of the Subscription tab. */
export function AgreementPanelView({ entity }: { entity: Customer360 }) {
  const [door, setDoor] = useState<string | null>(null);
  const [form, setForm] = useState<{ mode: 'amend' | 'rejoin'; base?: AgreementCard; from?: string } | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [resuming, setResuming] = useState(false);
  const q = useAgreementPanel(entity.id, door);
  const openChange = useOpenChange();
  const qc = useQueryClient();
  const idem = useIdempotencyKey();
  const refresh = () => qc.invalidateQueries({ predicate: (x) => [AGREEMENT_KEY, 'customer-calendar', 'customers', 'customer-changes', 'exceptions-days'].includes(String(x.queryKey[0])) });
  const cancel = useMutation({
    mutationFn: async ({ reason }: { reason: string }) => {
      if (pending?.kind === 'waiting') {
        const args = { p_subscription_id: pending.card.subscriptionId, p_reason: reason };
        return rpc<{ detail: string }>('cancel_scheduled_agreement', { ...args, p_idempotency_key: await idem.keyFor(args) });
      }
      const c = pending ? cancelOf(pending.row, entity.id) : null;
      const change = { ...c!.body, reason };
      return rpc<{ detail: string }>('cancel_agreement_exception', { p_cancel: change, p_idempotency_key: await idem.keyFor(change) });
    },
    onSuccess: async (r) => { idem.renew(); toast(r.detail, { tone: 'ok' }); setPending(null); await refresh(); },
    onError: (e) => { toast(plainError((e as Error).message), { tone: 'warn' }); },
  });
  const resume = useMutation({
    mutationFn: async ({ reason }: { reason: string }) => {
      const args = { p_customer_id: entity.id, p_from: q.data!.firstOpenDay, p_reason: reason, p_door: q.data!.door };
      return rpc<{ detail: string }>('resume_customer', { ...args, p_idempotency_key: await idem.keyFor(args) });
    },
    onSuccess: async (r) => { idem.renew(); toast(r.detail, { tone: 'ok' }); setResuming(false); await refresh(); },
    onError: (e) => { toast(plainError((e as Error).message), { tone: 'warn' }); },
  });
  if (q.isPending) return <Panel className="overflow-hidden"><PanelHeader title="Agreement" /><LoadingRows rows={4} columns={2} /></Panel>;
  if (q.error) return <Panel><ErrorState error={q.error} retry={() => void q.refetch()} /></Panel>;
  const data = q.data!;
  const rows = exceptionRows(data.exceptions, data.now);
  const left = data.customer.status.status === 'LEFT' || data.customer.status.status === 'INACTIVE';
  const paused = data.customer.status.status === 'PAUSED' || data.now?.status === 'PAUSED';
  return (
    <div className={cx('grid gap-4', form && 'lg:grid-cols-[minmax(0,1fr)_minmax(24rem,1fr)]')}>
      <div className="grid content-start gap-4">
        <Panel className="overflow-hidden">
          <AgreementHead data={data} door={door} onDoor={(d) => { setDoor(d); setForm(null); }} />
          <section aria-label="Now" className="grid gap-2 px-4 py-3">
            <h3 className="text-xs font-medium uppercase tracking-wide text-ink-muted">Now{data.now ? ` (from ${dayWord(data.now.from)}${data.now.until ? ` to ${dayWord(data.now.until)}` : ''})` : ''}</h3>
            {data.now ? <Lines card={data.now} /> : <EmptyState title="No standing agreement" detail={startWords(data, left, paused)} />}
            <div className="flex flex-wrap gap-2 pt-1">
              {data.commands.amend && data.may.amend && data.now && !left ? <Button variant="primary" size="dense" onClick={() => setForm({ mode: 'amend' })}><PencilLine size={13} aria-hidden /> Amend agreement</Button> : null}
              {data.may.days ? <ChangeDeliveryButton variant="secondary" label="Day only" /> : null}
              {data.may.days && !left && !paused ? <Button size="dense" onClick={() => openChange('pause')}><PauseCircle size={13} aria-hidden /> Pause</Button> : null}
              {data.commands.rejoin && data.may.amend && data.may.status && (left || paused || !data.now) ? <Button variant={!data.now ? 'primary' : undefined} size="dense" onClick={() => setForm({ mode: 'rejoin' })}><RotateCcw size={13} aria-hidden /> {left || paused ? 'Rejoin' : 'Start agreement'}</Button> : null}
            </div>
          </section>
          <section aria-label="Waiting for their date" className="grid gap-2 border-t border-line px-4 py-3">
            <h3 className="text-xs font-medium uppercase tracking-wide text-ink-muted">Waiting for their date</h3>
            {data.waiting.length === 0 ? <p className="text-small text-ink-muted">Nothing is waiting.</p> : data.waiting.map((w) => (
              <div key={w.subscriptionId} className="flex flex-wrap items-start justify-between gap-2">
                <div className="grid gap-1"><p className="text-small font-medium text-ink">From {dayWord(w.from)}<span className="code-text ml-2 font-normal text-ink-muted">{w.code}</span></p><Lines card={w} /></div>
                {data.may.amend ? <span className="flex gap-1">
                  {data.commands.amend ? <Button size="dense" onClick={() => setForm({ mode: 'amend', base: w, from: w.from })}>Change</Button> : null}
                  {data.commands.cancel ? <Button size="dense" onClick={() => setPending({ kind: 'waiting', card: w })}>Cancel</Button> : null}
                </span> : null}
              </div>))}
          </section>
          <section aria-label="Exceptions" className="grid gap-2 border-t border-line px-4 py-3">
            <h3 className="text-xs font-medium uppercase tracking-wide text-ink-muted">Exceptions, current and upcoming</h3>
            {rows.length === 0 ? <p className="text-small text-ink-muted">Every day goes by the agreement.</p> : (
              <ul className="grid gap-1">{rows.map((r) => {
                const c = data.commands.cancel && data.may.days ? cancelOf(r, entity.id) : null;
                return (
                  <li key={r.key} className="grid grid-cols-[minmax(7rem,auto)_minmax(0,1fr)_auto] items-center gap-x-3 text-body">
                    <span className="tabular-nums text-ink-muted">{r.when}</span>
                    <span>{r.words}{r.note ? <span className="ml-1 text-small text-ink-muted">· {r.note}</span> : null}</span>
                    <span>
                      {c ? <Button size="dense" onClick={() => setPending({ kind: 'exception', row: r })}>{c.label}</Button> : null}
                      {r.source[0]!.kind === 'HOLD' && data.may.days ? <Button size="dense" onClick={() => setResuming(true)}>Resume</Button> : null}
                    </span>
                  </li>);
              })}</ul>)}
          </section>
        </Panel>
        <AgreementHistoryPanel data={data} short />
      </div>
      {form ? <AgreementForm data={data} mode={form.mode} base={form.base} startFrom={form.from} onClose={() => setForm(null)} /> : null}
      <ConfirmDialog open={pending !== null} onClose={() => setPending(null)} title={pending?.kind === 'waiting' ? `Cancel the change from ${dayWord(pending.card.from)}` : `Cancel: ${pending?.row.when ?? ''} ${pending?.row.words ?? ''}`}
        consequence={pending?.kind === 'waiting' ? 'The agreement goes on as it is now. A hold that made this change ends with it.' : 'From the first day still open, the days go by the agreement again; a closed day stays. The round, the packet and the plant follow.'}
        confirmLabel="Cancel it" requireReason destructive onConfirm={async (reason) => { await cancel.mutateAsync({ reason: reason ?? '' }); }} />
      <ConfirmDialog open={resuming} onClose={() => setResuming(false)} title="Resume" consequence={`The usual items come back from ${dayWord(data.firstOpenDay)}.`} confirmLabel="Resume" requireReason
        onConfirm={async (reason) => { await resume.mutateAsync({ reason: reason ?? '' }); }} />
    </div>);
}

export function AgreementHistoryPanel({ data, short = false }: { data: AgreementPanelRead; short?: boolean }) {
  const [all, setAll] = useState(!short);
  const rows = all ? data.history : data.history.slice(0, 5);
  return (
    <Panel className="overflow-hidden">
      <PanelHeader title={<span className="inline-flex items-center gap-2"><HistoryIcon size={14} aria-hidden />History</span>} note="Everything that changed what they take, permanent changes included." />
      {data.history.length === 0 ? <EmptyState title="No changes yet" detail="A change to the agreement or to a day shows here." /> : (
        <table className="w-full text-small">
          <thead className="bg-surface-sunken text-left text-xs uppercase tracking-wide text-ink-muted">
            <tr><th className="px-3 py-2 font-medium">Made</th><th className="px-3 py-2 font-medium">What</th><th className="px-3 py-2 font-medium">For</th><th className="px-3 py-2 font-medium">Why</th><th className="px-3 py-2 font-medium">By</th></tr>
          </thead>
          <tbody>{rows.map((r, i) => (
            <tr key={`${r.at}${i}`} className={cx('border-t border-line', r.late && 'bg-queued-ground')}>
              <td className="whitespace-nowrap px-3 py-1.5">{dayWord(r.at)}</td>
              <td className="px-3 py-1.5">{historyWhat(r)}{r.kind === 'AGREEMENT' && r.state ? <span className="ml-1 text-ink-muted">{r.state}</span> : null}{r.late ? <span className="ml-1 text-xs text-queued">after the cut-off</span> : null}{r.state === 'CANCELLED' ? <span className="ml-1 text-xs text-ink-muted">cancelled</span> : null}</td>
              <td className="whitespace-nowrap px-3 py-1.5">{r.from ? spanWords(r.from, r.until ?? r.from) : '—'}</td>
              <td className="px-3 py-1.5">{r.why || '—'}</td>
              <td className="px-3 py-1.5">{r.by ?? '—'}</td>
            </tr>))}</tbody>
        </table>)}
      {!all && data.history.length > rows.length ? <div className="border-t border-line px-3 py-2"><Button size="dense" onClick={() => setAll(true)}>All {data.history.length}</Button></div> : null}
    </Panel>);
}

/** Subscription › History: the same read, in full. */
export function AgreementHistoryView({ entity }: { entity: Customer360 }) {
  const q = useAgreementPanel(entity.id, null);
  if (q.isPending) return <Panel className="overflow-hidden"><PanelHeader title="History" /><LoadingRows rows={6} columns={5} /></Panel>;
  if (q.error) return <Panel><ErrorState error={q.error} retry={() => void q.refetch()} /></Panel>;
  return <AgreementHistoryPanel data={q.data!} />;
}
